import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Check, Award, Mic, Globe2, Headphones, Clock, 
  Users, Sparkles, Target, Heart, Shield, Play
} from 'lucide-react';
import { siteConfig } from '../data/mock';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-[#0D0D0D] to-[#0A0A0A] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-[#C9A962]/5 blur-[150px] rounded-full pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div>
              <span className="text-[#C9A962] text-sm uppercase tracking-widest">About</span>
              <h1 className="heading-hero mt-4 mb-6">
                Experience You<br />
                <span className="text-[#C9A962]">Can Hear</span>
              </h1>
              <div className="space-y-6">
                <p className="body-large">
                  Joy Ogbekene is an award-winning African female voice talent with 
                  over two decades of professional experience in the voiceover industry.
                </p>
                <p className="body-medium text-[#707070]">
                  Her work spans Nigerian, African, and international brands across 
                  television, radio, digital media, and corporate platforms.
                </p>
                <p className="body-medium text-[#707070]">
                  Joy is known for her ability to adapt tone, pace, and emotion—delivering 
                  voiceovers that feel authentic, relatable, and culturally aligned.
                </p>
                <p className="body-medium text-[#707070]">
                  She records from a professional home studio, offering fast turnaround 
                  without compromising quality.
                </p>
              </div>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-6 mt-10 pt-10 border-t border-[#1A1A1A]">
                <div>
                  <div className="text-[#C9A962] text-3xl font-display font-semibold">20+</div>
                  <div className="text-[#707070] text-sm mt-1">Years Experience</div>
                </div>
                <div>
                  <div className="text-[#C9A962] text-3xl font-display font-semibold">500+</div>
                  <div className="text-[#707070] text-sm mt-1">Projects Delivered</div>
                </div>
                <div>
                  <div className="text-[#C9A962] text-3xl font-display font-semibold">3</div>
                  <div className="text-[#707070] text-sm mt-1">Languages</div>
                </div>
              </div>
            </div>

            {/* Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src={siteConfig.heroImage}
                  alt="Joy Ogbekene - Professional Voice Artist"
                  className="w-full aspect-[4/5] object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D]/40 via-transparent to-transparent" />
              </div>
              {/* Decorative Elements */}
              <div className="absolute -bottom-6 -right-6 w-48 h-48 border border-[#C9A962]/20 rounded-2xl -z-10" />
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#C9A962]/5 rounded-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* The Voice Behind the Brand */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-[#C9A962] text-sm uppercase tracking-widest">The Voice</span>
            <h2 className="heading-1 mt-4 mb-6">Behind the Microphone</h2>
            <p className="body-large">
              More than just a voice—Joy brings strategic understanding, emotional intelligence, 
              and cultural authenticity to every project she undertakes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Story Card 1 */}
            <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8">
              <div className="w-12 h-12 bg-[#C9A962]/10 rounded-xl flex items-center justify-center mb-6">
                <Heart size={24} className="text-[#C9A962]" />
              </div>
              <h3 className="heading-3 mb-4">A Passion for Storytelling</h3>
              <p className="body-medium text-[#707070]">
                Joy's journey into voiceover began with a deep love for storytelling and communication. 
                What started as a fascination with radio grew into a fulfilling career spanning 
                commercials, documentaries, corporate narration, and beyond. Every script is an 
                opportunity to tell a story that resonates.
              </p>
            </div>

            {/* Story Card 2 */}
            <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8">
              <div className="w-12 h-12 bg-[#C9A962]/10 rounded-xl flex items-center justify-center mb-6">
                <Globe2 size={24} className="text-[#C9A962]" />
              </div>
              <h3 className="heading-3 mb-4">African Voice, Global Reach</h3>
              <p className="body-medium text-[#707070]">
                With roots in Nigeria and a voice that transcends borders, Joy bridges the gap between 
                local authenticity and international appeal. Her ability to deliver in neutral English, 
                African-accented English, and Nigerian Pidgin makes her a versatile choice for brands 
                targeting diverse audiences.
              </p>
            </div>

            {/* Story Card 3 */}
            <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8">
              <div className="w-12 h-12 bg-[#C9A962]/10 rounded-xl flex items-center justify-center mb-6">
                <Target size={24} className="text-[#C9A962]" />
              </div>
              <h3 className="heading-3 mb-4">Understanding Your Message</h3>
              <p className="body-medium text-[#707070]">
                Joy doesn't just read scripts—she interprets them. Before recording, she takes time 
                to understand your brand, your audience, and your objectives. This approach ensures 
                that every word carries the right weight, tone, and emotional impact.
              </p>
            </div>

            {/* Story Card 4 */}
            <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8">
              <div className="w-12 h-12 bg-[#C9A962]/10 rounded-xl flex items-center justify-center mb-6">
                <Sparkles size={24} className="text-[#C9A962]" />
              </div>
              <h3 className="heading-3 mb-4">Beyond Voice: Direction & Consulting</h3>
              <p className="body-medium text-[#707070]">
                Drawing from years of industry experience, Joy also works as a Voiceover Director 
                and Consultant, helping clients and fellow artists refine their audio presence. 
                Her expertise extends beyond performance to shaping memorable audio identities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Strengths Section */}
      <section className="py-24 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#C9A962] text-sm uppercase tracking-widest">Expertise</span>
            <h2 className="heading-1 mt-4">Core Strengths</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Languages Card */}
            <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8 hover:border-[#C9A962]/20 transition-colors">
              <div className="w-12 h-12 bg-[#C9A962]/10 rounded-xl flex items-center justify-center mb-6">
                <Globe2 size={24} className="text-[#C9A962]" />
              </div>
              <h3 className="heading-3 mb-4">Languages & Accents</h3>
              <ul className="space-y-3">
                {['Neutral English Accent', 'African-Accented English', 'Nigerian Pidgin'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-[#B8B8B8]">
                    <Check size={16} className="text-[#C9A962] flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Voice Work Card */}
            <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8 hover:border-[#C9A962]/20 transition-colors">
              <div className="w-12 h-12 bg-[#C9A962]/10 rounded-xl flex items-center justify-center mb-6">
                <Mic size={24} className="text-[#C9A962]" />
              </div>
              <h3 className="heading-3 mb-4">Voice Work</h3>
              <ul className="space-y-3">
                {['Commercial Voiceover', 'Corporate Narration', 'IVR & Telephone Systems', 'Documentary Narration'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-[#B8B8B8]">
                    <Check size={16} className="text-[#C9A962] flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Consulting Card */}
            <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-8 hover:border-[#C9A962]/20 transition-colors">
              <div className="w-12 h-12 bg-[#C9A962]/10 rounded-xl flex items-center justify-center mb-6">
                <Award size={24} className="text-[#C9A962]" />
              </div>
              <h3 className="heading-3 mb-4">Additional Services</h3>
              <ul className="space-y-3">
                {['Audio Branding', 'Voiceover Direction', 'Consulting', 'Script Review'].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-[#B8B8B8]">
                    <Check size={16} className="text-[#C9A962] flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Voice Characteristics */}
      <section className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#C9A962] text-sm uppercase tracking-widest">Voice Profile</span>
            <h2 className="heading-1 mt-4">Voice Characteristics</h2>
            <p className="body-large mt-4 max-w-2xl mx-auto">
              Joy's voice is versatile enough to adapt to any brief while maintaining 
              its signature warmth and authority.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              'Warm', 'Authoritative', 'Conversational', 'Confident',
              'Friendly', 'Professional', 'Engaging', 'Trustworthy',
              'Energetic', 'Calm', 'Relatable', 'Articulate'
            ].map((trait) => (
              <div 
                key={trait}
                className="bg-[#161616] border border-[#1A1A1A] rounded-xl px-4 py-3 text-center hover:border-[#C9A962]/30 hover:bg-[#1A1A1A] transition-all"
              >
                <span className="text-[#B8B8B8] text-sm">{trait}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Working Process */}
      <section className="py-24 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#C9A962] text-sm uppercase tracking-widest">Process</span>
            <h2 className="heading-1 mt-4">How We Work Together</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-[#C9A962]/10 border border-[#C9A962]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-[#C9A962] text-2xl font-display font-semibold">1</span>
              </div>
              <h4 className="text-white font-medium mb-2">Brief & Discovery</h4>
              <p className="text-[#707070] text-sm">
                Share your project details, target audience, and desired tone.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-[#C9A962]/10 border border-[#C9A962]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-[#C9A962] text-2xl font-display font-semibold">2</span>
              </div>
              <h4 className="text-white font-medium mb-2">Quote & Agreement</h4>
              <p className="text-[#707070] text-sm">
                Receive a custom quote based on usage, length, and timeline.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-[#C9A962]/10 border border-[#C9A962]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-[#C9A962] text-2xl font-display font-semibold">3</span>
              </div>
              <h4 className="text-white font-medium mb-2">Recording & Review</h4>
              <p className="text-[#707070] text-sm">
                Professional recording with revisions included as needed.
              </p>
            </div>

            {/* Step 4 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-[#C9A962]/10 border border-[#C9A962]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-[#C9A962] text-2xl font-display font-semibold">4</span>
              </div>
              <h4 className="text-white font-medium mb-2">Final Delivery</h4>
              <p className="text-[#707070] text-sm">
                Receive studio-quality files ready for your production.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 bg-[#0A0A0A] relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-96 h-96 bg-[#C9A962]/5 rounded-full blur-[120px]" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <span className="text-[#C9A962] text-sm uppercase tracking-widest">Philosophy</span>
          <blockquote className="mt-8">
            <p className="font-display text-3xl md:text-4xl text-white leading-relaxed italic">
              "A great voiceover doesn't just sound good — it builds 
              <span className="text-[#C9A962]"> trust</span>, 
              <span className="text-[#C9A962]"> clarity</span>, and 
              <span className="text-[#C9A962]"> connection</span>."
            </p>
          </blockquote>
          <div className="mt-8">
            <p className="text-[#B8B8B8] font-medium">Joy Ogbekene</p>
            <p className="text-[#707070] text-sm">Professional Voice Artist</p>
          </div>
        </div>
      </section>

      {/* Studio & Technical */}
      <section className="py-24 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#C9A962] text-sm uppercase tracking-widest">Studio</span>
              <h2 className="heading-1 mt-4 mb-6">Professional Setup</h2>
              <p className="body-large mb-6">
                Joy records from a fully-equipped professional home studio, ensuring 
                broadcast-quality audio for every project.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C9A962]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Headphones size={20} className="text-[#C9A962]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Studio-Quality Sound</h4>
                    <p className="text-[#707070] text-sm mt-1">
                      Acoustically treated recording space with professional-grade equipment.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C9A962]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock size={20} className="text-[#C9A962]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Fast Turnaround</h4>
                    <p className="text-[#707070] text-sm mt-1">
                      Most projects delivered within 24-48 hours. Rush delivery available.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C9A962]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield size={20} className="text-[#C9A962]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Reliable & Consistent</h4>
                    <p className="text-[#707070] text-sm mt-1">
                      Dependable delivery, consistent quality, and professional communication.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-6 text-center">
                <div className="text-[#C9A962] text-2xl font-display font-semibold">24-48h</div>
                <div className="text-[#707070] text-sm mt-1">Standard Delivery</div>
              </div>
              <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-6 text-center">
                <div className="text-[#C9A962] text-2xl font-display font-semibold">Same Day</div>
                <div className="text-[#707070] text-sm mt-1">Rush Available</div>
              </div>
              <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-6 text-center">
                <div className="text-[#C9A962] text-2xl font-display font-semibold">WAV/MP3</div>
                <div className="text-[#707070] text-sm mt-1">File Formats</div>
              </div>
              <div className="bg-[#161616] border border-[#1A1A1A] rounded-2xl p-6 text-center">
                <div className="text-[#C9A962] text-2xl font-display font-semibold">2 Rounds</div>
                <div className="text-[#707070] text-sm mt-1">Revisions Included</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#0A0A0A] border-t border-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="heading-1 mb-6">Let's Work Together</h2>
          <p className="body-large mb-12 max-w-xl mx-auto">
            Ready to add a professional African voice to your next project?
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/demos"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C9A962] text-[#0D0D0D] font-medium rounded-full hover:bg-[#E5D4A1] transition-all duration-300 hover:shadow-[0_0_30px_rgba(201,169,98,0.3)]"
            >
              <Play size={18} />
              Listen to Demos
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border border-[#2A2A2A] text-white font-medium rounded-full hover:border-[#C9A962] hover:text-[#C9A962] transition-all duration-300"
            >
              Get in Touch
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
